/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Component to filter date
 *
 * @author Vlaidslav Oshkanov
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import { createRef, PureComponent } from 'react';

import moment from 'moment';
import type { Moment } from 'moment';

import { icons } from '@assets/index';
import { DropDownFilter, RangeDatePicker } from '@components/common';
import { EInputKind } from '@components/common/Input/Input';
import initTranslationCommon from '@modules/internationalization/constants/common';
import type { ICommonTranslation } from '@modules/internationalization/types';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';

/**
 * @description Date filter props
 * @interface IFilterDateProps
 * @property {moment.Moment} startDate - range start date
 * @property {moment.Moment} endDate - range end date
 * @property {moment.Moment} range - range
 * @property {string} filterValue - filter label value
 * @property {() => void} onChange - change method
 * @property {object} messages - locale variables
 */

/**
 * @description Date filter params
 * @interface IFilterDateParams
 * @property {moment.Moment} startDate - range start date
 * @property {moment.Moment} endDate - range end date
 * @property {string} filterValue - filter label value
 */
export interface IFilterDateParams {
    filterValue: string;
    startDate: string | undefined;
    endDate: string | undefined;
}

export interface IFilterDateProps {
    endDate: moment.Moment;
    filterValue: string;
    hideTimePicker?: boolean;
    onChange?: (argument: IFilterDateParams) => void;
    range: moment.Moment | undefined;
    startDate: moment.Moment;
    translation: ICommonTranslation;
}

/**
 * @description Date filter state
 * @interface IFilterDateState
 * @property {moment.Moment} startDate - range start date
 * @property {moment.Moment} endDate - range end date
 * @property {string} filterValue - filter label value
 */
interface IFilterDateState {
    endDate: Moment;
    filterValue: string | undefined;
    startDate: Moment;
}

const calendar = moment();
const startDate = calendar.clone().startOf('month');
const endDate = calendar.clone().endOf('month');

const defaultProps: Partial<IFilterDateProps> = {
    endDate: endDate,
    filterValue: '',
    range: startDate,
    startDate: startDate,
    translation: initTranslationCommon,
};

/**
 * @description Filter for dates.
 * @class FilterDate
 * @extends PureComponent<IFilterDateProps>
 *
 * @see BlockWithIcon component
 * @see RangeDatePicker component
 * @see TextBlock component
 */
class FilterDate extends PureComponent<IFilterDateProps, IFilterDateState> {

    public calendarRef: React.RefObject<RangeDatePicker> = createRef<RangeDatePicker>();

    static defaultProps = defaultProps;

    public state = {
        endDate: this.props.endDate ? this.props.endDate : moment(),
        filterValue: '',
        startDate: this.props.startDate ? this.props.startDate : moment(),
    };

    public okButtonHandler = (): void => {
        const calendar = this.calendarRef.current;
        if (calendar) {
            calendar.onApply();
        }
    };

    public calendarRangeHandler = (leftRange: Moment, rightRange: Moment): void => {
        const filterValue = getDateFilterValue(leftRange, rightRange);
        const isFilterChanges = this.isFilterChanged(leftRange, rightRange);
        if (this.props.onChange && isFilterChanges) {
            this.props.onChange({
                endDate: rightRange.format(EFormatData.FullDataWithTimeZone),
                filterValue,
                startDate: leftRange.format(EFormatData.FullDataWithTimeZone),
            });
        }
    };

    public render() {
        const {
            endDate,
            filterValue,
            startDate,
            translation,
        } = this.props;
        const start = moment(startDate);
        const end = moment(endDate);
        const timeLeftHour = parseInt(start.format(EFormatData.OnlyHour));
        const timeLeftMin = parseInt(start.format(EFormatData.OnlyMinutes));
        const timeRightHour = parseInt(end.format(EFormatData.OnlyHour));
        const timeRightMin = parseInt(end.format(EFormatData.OnlyMinutes));
        return (
            <DropDownFilter
                filterButtonLabel={filterValue}
                icon={icons.calendarIcon}
                iconOpened={icons.calendarIcon}
                kind={EInputKind.normal}
                renderInBody={true}
                showCancelBtn={true}
                translation={translation.dropDown}
                widthAuto={true}
                onOkClick={this.okButtonHandler}
            >
                <RangeDatePicker
                    {...this.props}
                    endDate={end}
                    messages={translation.calendar}
                    ref={this.calendarRef}
                    startDate={start}
                    timeLeftHour={timeLeftHour}
                    timeLeftMin={timeLeftMin}
                    timePicker={!this.props.hideTimePicker}
                    timeRightHour={timeRightHour}
                    timeRightMin={timeRightMin}
                    onApply={this.calendarRangeHandler}
                />
            </DropDownFilter>
        );
    }

    private isFilterChanged = (leftRange: Moment, rightRange: Moment): boolean =>
        !leftRange.isSame(this.state.startDate)
        || !rightRange.isSame(this.state.endDate)
        || !this.props.filterValue;

}

export const getDateFilterValue = (leftRange: Moment, rightRange: Moment): string => {
    if (!leftRange || !rightRange) {
        return '';
    }
    const startMonth = leftRange.format(EFormatData.FullDataOnlyMonth);
    const startDay = leftRange.format(EFormatData.FullDataOnlyDay);
    const startYear = leftRange.format(EFormatData.FullDataOnlyYear);

    const endMonth = rightRange.format(EFormatData.FullDataOnlyMonth);
    const endDay = rightRange.format(EFormatData.FullDataOnlyDay);
    const endYear = rightRange.format(EFormatData.FullDataOnlyYear);

    const start = `${startDay}.${startMonth}.${startYear}`;
    const end = `${endDay}.${endMonth}.${endYear}`;
    return `${start} ... ${end}`;
};

export default FilterDate;
